import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { Contact, ContactCreate, ContactUpdate, CompanyType, Gender } from '../types/contact';
import api from '../config/axios';
import { CONTACTS } from '../config/apiPaths';
import { notifyEntityCreated, notifyEntityUpdated } from '../utils/entityNotifications';

interface ContactState {
  contacts: Contact[];
  currentContact: Contact | null;
  loading: boolean;
  error: string | null;
  fetchContacts: (organizationId?: number) => Promise<void>;
  getContact: (id: number) => Promise<Contact | undefined>;
  createContact: (contact: ContactCreate) => Promise<Contact>;
  updateContact: (id: number, updates: ContactUpdate) => Promise<void>;
  deleteContact: (id: number) => Promise<void>;
  setCurrentContact: (contact: Contact | null) => void;
  clearCurrentContact: () => void;
}

const useContactStore = create<ContactState>()(
  devtools(
    (set, get) => ({
      contacts: [],
      currentContact: null,
      loading: false,
      error: null,

      fetchContacts: async (organizationId?: number) => {
        set({ loading: true, error: null });
        try {
          const params = organizationId ? { organization_id: organizationId } : {};
          const response = await api.get<Contact[]>(CONTACTS.ALL, { params });
          // Ensure all contact data is properly formatted
          const formattedContacts = response.data.map(contact => ({
            ...contact,
            first_name: String(contact.first_name || ''),
            last_name: String(contact.last_name || ''),
            email: String(contact.email || ''),
            phone: String(contact.phone || ''),
            job_title: contact.job_title ? String(contact.job_title) : undefined,
            company: contact.company ? String(contact.company) : undefined,
            company_type: contact.company_type ? contact.company_type as CompanyType : undefined,
            gender: contact.gender ? contact.gender as Gender : undefined,
            profile_picture: contact.profile_picture ? String(contact.profile_picture) : undefined,
          }));
          set({ contacts: formattedContacts, loading: false });
        } catch (error) {
          set({ 
            error: error instanceof Error ? error.message : 'Failed to fetch contacts',
            loading: false 
          });
          throw error; // Re-throw to let the notification system handle it
        }
      },

      getContact: async (id: number) => {
        console.log(`[ContactStore] Fetching contact with ID: ${id}`);
        set({ loading: true, error: null });
        try {
          const response = await api.get<Contact>(CONTACTS.BY_ID(id));
        
          
          // Ensure the contact data is properly formatted
          const formattedContact = {
            ...response.data,
            first_name: String(response.data.first_name || ''),
            last_name: String(response.data.last_name || ''),
            email: String(response.data.email || ''),
            phone: String(response.data.phone || ''),
            job_title: response.data.job_title ? String(response.data.job_title) : undefined,
            company: response.data.company ? String(response.data.company) : undefined,
            company_type: response.data.company_type ? response.data.company_type as CompanyType : undefined,
            gender: response.data.gender ? response.data.gender as Gender : undefined,
            profile_picture: response.data.profile_picture ? String(response.data.profile_picture) : undefined,
          };
          
        
          set({ currentContact: formattedContact, loading: false });
          return formattedContact;
        } catch (error) {
          set({ 
            error: error instanceof Error ? error.message : 'Failed to fetch contact',
            loading: false 
          });
          return undefined;
        }
      },

      createContact: async (contact: ContactCreate) => {
        set({ loading: true, error: null });
        try {
          // Organization ID will be set by the backend handler from auth context
          const createPayload = { ...contact };
          
          const response = await api.post<Contact>(CONTACTS.ALL, createPayload);
          set((state) => ({
            contacts: [...state.contacts, response.data],
            loading: false,
          }));
          
          // Notify about the new contact creation
          notifyEntityCreated('contact', {
            id: response.data.id,
            first_name: response.data.first_name,
            last_name: response.data.last_name,
          });
          
          return response.data;
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to create contact',
            loading: false,
          });
          throw error;
        }
      },

      updateContact: async (id: number, updates: ContactUpdate) => {
        set({ loading: true, error: null });
        try {
          await api.put(CONTACTS.BY_ID(id), updates);
          
          // Get current state for notification
          const currentState = useContactStore.getState();
          const existingContact = currentState.contacts.find(c => c.id === id);
          
          set((state) => ({
            contacts: state.contacts.map((contact) =>
              contact.id === id ? { ...contact, ...updates } : contact
            ),
            currentContact: state.currentContact?.id === id 
              ? { ...state.currentContact, ...updates } 
              : state.currentContact,
            loading: false,
          }));
          
          // Notify about the contact update
          if (existingContact) {
            notifyEntityUpdated('contact', {
              id: existingContact.id,
              first_name: updates.first_name || existingContact.first_name,
              last_name: updates.last_name || existingContact.last_name,
            });
          }
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to update contact',
            loading: false,
          });
          throw error;
        }
      },

      deleteContact: async (id: number) => {
        set({ loading: true, error: null });
        try {
          await api.delete(CONTACTS.BY_ID(id));
          set((state) => ({
            contacts: state.contacts.filter((contact) => contact.id !== id),
            currentContact: state.currentContact?.id === id ? null : state.currentContact,
            loading: false,
          }));
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to delete contact',
            loading: false,
          });
          throw error;
        }
      },

      setCurrentContact: (contact: Contact | null) => {
        set({ currentContact: contact });
      },

      clearCurrentContact: () => {
        set({ currentContact: null });
      },
    }),
    {
      name: 'contact-storage',
    }
  )
);

export { useContactStore };
export default useContactStore;